import * as React from "react";
import { Grid, Typography, Button, Box } from "@mui/material";
import { Formik } from "formik";
import { useMemo, useState } from "react";
import * as yup from "yup";
import { v4 as uuidv4 } from 'uuid';
import OZInput from "../../../components/admin/common/OZInput";
import { ADMIN_FORM_MODES } from "../../../utils/constants";
import OZDialog from "../../../components/admin/common/OZDialog";
import { showErrorNotification } from "../../../components/admin/common/NotiService";
import OZSelect from "../../../components/admin/common/OZSelect";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import OZFCheckbox from "../../../components/admin/common/OZFCheckbox";

const ManageWidgetsForm = (widgetFormProps) => {
    let widgetFormInitialMode = widgetFormProps.data ? ADMIN_FORM_MODES.edit : ADMIN_FORM_MODES.add;
    widgetFormInitialMode = widgetFormProps?.openDialog === true ? ADMIN_FORM_MODES.edit : widgetFormInitialMode;
    const [openAddDialog, setOpenAddDialog] = useState(true);
    const [widgetFormMode] = useState(widgetFormInitialMode);

    const closeWidgetForm = () => {
        try {
            widgetFormProps.onClose();
            setOpenAddDialog(false);
        } catch (error) {
            showErrorNotification(error.message || "Error in closing widget form");
        }
    };

    return (
        <div>
            {widgetFormMode === ADMIN_FORM_MODES.add ? (
                <OZDialog
                    title="Add Widget"
                    saveButtonProps={{
                        form: 'manageWidgetForm',
                        type: 'submit'
                    }}
                    open={openAddDialog}
                    mode={widgetFormMode}
                    onClose={closeWidgetForm}
                >
                    <DataForm
                        widgetFormMode={widgetFormMode}
                        addWidget={widgetFormProps.addWidget}
                        updateWidget={widgetFormProps.updateWidget}
                        closeWidgetForm={closeWidgetForm}
                    />
                </OZDialog>
            ) : (
                <OZDialog
                    title="Edit Widget"
                    saveButtonProps={{
                        form: 'manageWidgetForm',
                        type: 'submit'
                    }}
                    open={openAddDialog}
                    mode={widgetFormMode}
                    onClose={closeWidgetForm}
                    hideSaveButton={!(widgetFormProps.editAccess) || false}
                >
                    <DataForm
                        editData={widgetFormProps.data}
                        widgetFormMode={widgetFormMode}
                        addWidget={widgetFormProps.addWidget}
                        updateWidget={widgetFormProps.updateWidget}
                        closeWidgetForm={closeWidgetForm}
                    />
                </OZDialog>
            )}
        </div>
    );
};

function DataForm({ editData, widgetFormMode, addWidget, updateWidget, closeWidgetForm }) {

    const validationSchema = useMemo(
        () =>
            yup.object({
                code: yup
                    .string("Enter Widget Code")
                    .required("Widget Code is required")
                    .min(2, "Widget Code should be between 2 to 50 characters long")
                    .max(50, "Widget Code should be between 2 to 50 characters long"),
                name: yup
                    .string("Enter Widget Name")
                    .required("Widget Name is required")
                    .min(2, "Widget Name should be between 2 to 100 characters long")
                    .max(100, "Widget Name should be between 2 to 100 characters long"),
                description: yup
                    .string("Enter Widget Description")
                    .required("Widget Description is required")
                    .min(2, "Widget Description should be between 2 to 500 characters long")
                    .max(500, "Widget Description should be between 2 to 500 characters long"),
                refreshInterval: yup
                    .number()
                    .required("Refresh interval is required")
                    .oneOf([0, 5, 10, 15, 20, 30, 60, 120, 180, 240, 300], "Invalid refresh interval"),
                apiId: yup
                    .number()
                    .nullable()
                    .optional(),
                widgetParams: yup
                    .array()
                    .nullable()
            }),
        []
    );

    let widgetFormInitialData = editData
        ? {
            ...editData,
            refreshInterval: editData.refreshInterval || 0
        }
        : {
            code: "",
            name: "",
            description: "",
            refreshInterval: 0,
            apiId: null,
            isActive: true,
            widgetParams: []
        };

    const refreshIntervalOptions = useMemo(() => [0, 5, 10, 15, 20, 30, 60, 120, 180, 240, 300], []);

    return (
        <div className="ozManageWidgetAddForm">
            <Formik
                initialValues={widgetFormInitialData}
                validationSchema={validationSchema}
                onSubmit={(userEnteredValues) => {
                    const formValues = { ...userEnteredValues };
                    console.log("userEnteredValues", userEnteredValues);

                    // Convert widgetParams array back to object
                    const paramsObject = {};
                    if (formValues.widgetParams && Array.isArray(formValues.widgetParams)) {
                        formValues.widgetParams.forEach(param => {
                            if (param.key && param.key.trim() !== '') {
                                paramsObject[param.key] = param.value;
                            }
                        });
                    }
                    formValues.widgetParams = JSON.stringify(paramsObject);

                    if (widgetFormMode === ADMIN_FORM_MODES.add) {
                        addWidget(formValues, closeWidgetForm);
                    } else {
                        updateWidget(formValues, closeWidgetForm);
                    }
                }}
            >
                {(formik) => (
                    <form
                        onSubmit={formik.handleSubmit}
                        id="manageWidgetForm"
                        className="ozcwScrollbar oz-manage-widget-form-container"
                    >
                        <Grid container={true} spacing={2}>
                            <Grid item xs={12} sm={6} md={6} lg={6}>
                                <OZInput
                                    name="code"
                                    placeholder="Enter Widget Code (e.g., Agent List)"
                                    label="Widget Code *"
                                    disabled={widgetFormMode === ADMIN_FORM_MODES.edit}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={6} lg={6}>
                                <OZInput
                                    name="name"
                                    placeholder="Enter Widget Name"
                                    label="Widget Name *"
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={12} lg={12}>
                                <OZInput
                                    name="description"
                                    placeholder="Enter Widget Description"
                                    label="Widget Description *"
                                    multiline={true}
                                    maxRows={4}
                                    height='auto'
                                    inputProps={{ style: { height: '60px' } }}
                                />
                            </Grid>

                            <Grid item xs={6} sm={6} md={6} lg={6}>
                                <OZSelect
                                    name="refreshInterval"
                                    label="Refresh Interval (seconds) *"
                                    options={refreshIntervalOptions}
                                    placeholder="Select refresh interval"
                                />
                                <Typography
                                    sx={{
                                        fontSize: '11px',
                                        color: '#666',
                                        marginTop: '4px',
                                        marginLeft: '2px'
                                    }}
                                >
                                    For Manual Refresh, select 0
                                </Typography>
                            </Grid>

                            <Grid item xs={6} sm={6} md={6} lg={6}>
                                <OZInput
                                    name="apiId"
                                    placeholder="Enter API ID (optional)"
                                    label="API ID"
                                    type="number"
                                />
                            </Grid>

                            {/* <Grid sx={{ display: 'flex', alignItems: 'center', ml: 1, mb: .5 }} item xs={12} sm={12} md={12} lg={12}>
                                <OZFCheckbox
                                    labelPosition="end"
                                    name="isActive"
                                    label="Active Widget"
                                />
                            </Grid> */}

                            <Grid item xs={12}>
                                <Typography
                                    fontSize='12px'
                                    fontWeight={400}
                                    lineHeight='14px'
                                    marginBottom={'6px'}
                                    marginLeft='1px'
                                    color="#212121"
                                >
                                    Widget Parameters
                                </Typography>
                                <JsonEditor
                                    formik={formik}
                                />
                            </Grid>
                        </Grid>
                    </form>
                )}
            </Formik>
        </div>
    );
};

function JsonEditor({ formik }) {

    const params = useMemo(() => formik?.values?.widgetParams || [], [formik?.values?.widgetParams]);

    const addParameterPair = () => {
        try {
            formik?.setFieldValue('widgetParams', [...(params), { key: '', value: '', id: uuidv4() }]);
        } catch (error) {
            showErrorNotification(error.message || "Error in adding parameter pair");
        }
    };

    const removeParameterPair = (id) => {
        try {
            if (params.length > 0) {
                formik?.setFieldValue('widgetParams', params.filter(pair => pair.id !== id));
            }
        } catch (error) {
            showErrorNotification(error.message || "Error in removing parameter pair");
        }
    };

    return (
        <Box sx={{
            border: '1px solid #E6E5E6',
            borderRadius: '8px',
            padding: '16px',
            backgroundColor: '#FAFAFA'
        }}>
            <Typography
                sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '14px',
                    color: '#666666',
                    marginBottom: '12px'
                }}
            >
                Add key-value pairs for widget default parameters.
            </Typography>

            <Box sx={{ marginBottom: '16px' }}>
                {params && Array.isArray(params) && params.length > 0 && params.map((pair, index) => (
                    <Box key={pair.id} sx={{
                        display: 'flex',
                        gap: '12px',
                        marginBottom: '12px',
                        alignItems: 'flex-start'
                    }}>
                        <Box sx={{ flex: 1 }}>
                            <OZInput
                                name={`widgetParams.${index}.key`}
                                placeholder="Parameter Key (e.g., width, height)"
                                label="Parameter Key"
                                value={pair.key}
                                size="small"
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <OZInput
                                name={`widgetParams.${index}.value`}
                                placeholder="Parameter Value (e.g., 12, true)"
                                label="Parameter Value"
                                value={pair.value}
                                size="small"
                            />
                        </Box>
                        <Button
                            onClick={() => removeParameterPair(pair.id)}
                            disabled={params.length === 0}
                            sx={{
                                minWidth: '40px',
                                height: '40px',
                                padding: '8px',
                                marginTop: '20px',
                                borderRadius: '8px',
                                border: '1px solid #E6E5E6',
                                backgroundColor: params.length === 0 ? '#F5F5F5' : '#FFFFFF',
                                color: params.length === 0 ? '#CCCCCC' : '#FF4444',
                                '&:hover': {
                                    backgroundColor: params.length === 0 ? '#F5F5F5' : '#FFE6E6',
                                    border: params.length === 0 ? '1px solid #E6E5E6' : '1px solid #FF4444'
                                }
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </Button>
                    </Box>
                ))}
            </Box>

            <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addParameterPair}
                fullWidth
                sx={{
                    height: '36px',
                    borderRadius: '8px',
                    border: '1px solid #3D8BF8',
                    backgroundColor: '#FFFFFF',
                    color: '#3D8BF8',
                    fontSize: '12px',
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:hover': {
                        backgroundColor: '#F0F7FF',
                        border: '1px solid #3D8BF8'
                    }
                }}
            >
                Add Parameter
            </Button>
        </Box>
    );
}

export default ManageWidgetsForm;